// client/src/components/itensVenda/ItemVendaForm.js - VERSÃO 100% BLINDADA E COM CAST DE ARRAY DE IMAGENS CORRIGIDO
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMutateItensVenda } from '../../hooks/useItensVenda';
import useAlert from '../../hooks/useAlert';
import ItemVendaImages from './ItemVendaImages';
import {
    XCircle, Loader2, Package, Scale, Box, Barcode, Truck, DollarSign,
    Hash, Type, List, AlertCircle, Plus, Minus
} from 'lucide-react';
import { useUnidadesParaFormulario } from '../../hooks/useUnidades';
import { useCategoriasParaFormulario } from '../../hooks/useCategorias';
import { useFornecedoresParaFormulario } from '../../hooks/useFornecedores';
import { useAuth } from '../../context/auth/AuthProvider';
import { useTenantI18n } from '../../hooks/useAccounts';
import { getCurrencySymbol } from '../../utils/formatters';
import { FormStepper, FormStepNavigation } from '../shared/FormStepper';

const STEP_IDS = ['basicos', 'detalhes', 'unidades'];
const STEP_LABELS = ['Informações Básicas', 'Detalhes do Produto', 'Preço e Estoque'];

const isValidObjectId = (id) => {
    if (!id || typeof id !== 'string') return false;
    return /^[0-9a-fA-F]{24}$/.test(id);
};

const isValidAccountId = (accountId) => {
    return accountId && isValidObjectId(accountId);
};

// ✅ FUNÇÃO CRÍTICA ADICIONADA: Extrai o ID de forma segura não importa o formato do MongoDB
const extractId = (item) => {
    if (!item) return '';
    if (typeof item === 'string') return item;
    if (typeof item === 'object') {
        if (item.$oid) return String(item.$oid);
        if (item._id) return String(item._id);
        if (item.id) return String(item.id);
    }
    return String(item);
};

const defaultAvatar = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTUwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTUwIiBoZWlnaHQ9IjE1MCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9Ijc1IiB5PSI3NSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOWNhMGFiIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iMC4zNWVtIj5TZW0gSW1hZ2VtPC90ZXh0Pjwvc3ZnPg==';

const processarImagensParaPreview = (item) => {
    if (!item) {
        return null;
    }
    
    if (item.imagemPrincipal && item.imagemPrincipal.refId) {
        const principalRefId = item.imagemPrincipal.refId.toString();
        
        if (item.imagens && Array.isArray(item.imagens)) {
            for (let img of item.imagens) {
                if (!img) continue;
                const imgId = img._id || img.id;
                if (imgId && imgId.toString() === principalRefId) {
                    return img.url || img.arquivo || img.caminhoS3 || img.path || img.imagemUrl;
                }
            }
        }
        
        if (item.imagensDaGaleria && Array.isArray(item.imagensDaGaleria)) {
            for (let img of item.imagensDaGaleria) {
                if (!img) continue;
                const imgId = img._id || img.id;
                if (imgId && imgId.toString() === principalRefId) {
                    return img.url || img.arquivo || img.caminhoS3 || img.path || img.imagemUrl;
                }
            }
        }
    }
    
    if (item.imagens && item.imagens.length > 0) {
        const primeiraImagem = item.imagens[0];
        if (primeiraImagem && typeof primeiraImagem === 'object') {
            return primeiraImagem.url || primeiraImagem.arquivo || primeiraImagem.caminhoS3 || primeiraImagem.path || primeiraImagem.imagemUrl;
        }
    }
    
    if (item.imagensDaGaleria && item.imagensDaGaleria.length > 0) {
        const primeiraImagem = item.imagensDaGaleria[0];
        if (primeiraImagem && typeof primeiraImagem === 'object') {
            return primeiraImagem.url || primeiraImagem.arquivo || primeiraImagem.caminhoS3 || primeiraImagem.path || primeiraImagem.imagemUrl;
        }
    }
    
    if (item.imagemUrl) {
        return item.imagemUrl;
    }
    
    if (item.imagemPrincipal && item.imagemPrincipal.url) {
        return item.imagemPrincipal.url;
    }
    
    return null;
};

const normalizeItemData = (item) => {
    if (!item) return null;
    
    return {
        ...item,
        _id: item._id || item.id || null,
        nome: item.nome || '',
        descricao: item.descricao || '',
        sku: item.sku || '',
        categoria: item.categoria || '',
        controlaEstoque: item.controlaEstoque !== undefined ? item.controlaEstoque : true,
        ativo: item.ativo !== undefined ? item.ativo : true,
        visivelParaCliente: item.visivelParaCliente !== undefined ? item.visivelParaCliente : true,
        tags: Array.isArray(item.tags) ? item.tags : [],
        unidadesDetalhes: item.unidadesDetalhes || [],
        imagens: item.imagens || [],
        imagensDaGaleria: item.imagensDaGaleria || [],
        imagemPrincipal: item.imagemPrincipal || null,
        metadata: item.metadata || {
            tipoItem: 'fisico', peso: 0,
            dimensoes: { comprimento: 0, largura: 0, altura: 0 },
            marca: '', modelo: '', codigoBarras: '', fornecedor: '',
            custo: 0, margemLucro: 0
        }
    };
};

const initialState = {
    _id: null,
    nome: '',
    descricao: '',
    sku: '',
    categoria: '',
    controlaEstoque: true,
    ativo: true,
    visivelParaCliente: true,
    tags: [],
    unidadesDetalhes: [],
    imagens: [],
    imagensDaGaleria: [],
    imagemPrincipal: null,
    metadata: {
        tipoItem: 'fisico', peso: 0,
        dimensoes: { comprimento: 0, largura: 0, altura: 0 },
        marca: '', modelo: '', codigoBarras: '', fornecedor: '',
        custo: 0, margemLucro: 0
    }
};

const ItemVendaForm = ({ current, onCloseForm, onSaveSuccess }) => {
    const { addItemVenda, updateItemVenda } = useMutateItensVenda();
    const { setAlert } = useAlert();
    // ✅ ACHADO: rótulos abaixo eram fixos em "(R$)", sem ler
    // configuracoesGerais.i18n da conta.
    const { currency, locale } = useTenantI18n();
    const currencySymbol = getCurrencySymbol(currency, locale);
    const [item, setItem] = useState(initialState);
    const [currentStep, setCurrentStep] = useState(1);
    const goToTab = (tabId) => {
        const index = STEP_IDS.indexOf(tabId);
        if (index >= 0) setCurrentStep(index + 1);
    };
    const [novaTag, setNovaTag] = useState('');
    const [formErrors, setFormErrors] = useState({});
    const [isInitialized, setIsInitialized] = useState(false);
    const [imageUpdateTrigger, setImageUpdateTrigger] = useState(0);
    
    const normalizedCurrent = useMemo(() => normalizeItemData(current), [current]);
    const isEditing = !!normalizedCurrent && !!normalizedCurrent._id;
    
    const loadingMutation = addItemVenda.isPending || updateItemVenda.isPending;

    const [imagemPreview, setImagemPreview] = useState(defaultAvatar);

    const { user } = useAuth();
    const accountId = useMemo(() => {
        const id = user?.account?._id || user?.account?.id;
        return id;
    }, [user]);

    const { 
        data: unidadesData = {}, 
        isLoading: loadingUnidades, 
        isError: isErrorUnidadesQuery,
        error: errorUnidades
    } = useUnidadesParaFormulario(accountId, { enabled: isValidAccountId(accountId) });

    const { 
        data: categoriasDisponiveis = [],
        isLoading: loadingCategorias,
        isError: isErrorCategoriasQuery,
        error: errorCategorias
    } = useCategoriasParaFormulario(accountId, 'produto', { enabled: isValidAccountId(accountId) });

    const { 
        data: fornecedoresDisponiveis = [],
        isLoading: loadingFornecedores,
        isError: isErrorFornecedoresQuery,
        error: errorFornecedores
    } = useFornecedoresParaFormulario(accountId, { enabled: isValidAccountId(accountId) });

    const unidadesArray = useMemo(() => {
        if (Array.isArray(unidadesData)) return unidadesData;
        if (unidadesData?.data && Array.isArray(unidadesData.data)) return unidadesData.data;
        if (unidadesData?.success && Array.isArray(unidadesData.data)) return unidadesData.data;
        return [];
    }, [unidadesData]);

    const categoriasArray = useMemo(() => {
        if (Array.isArray(categoriasDisponiveis)) return categoriasDisponiveis;
        if (categoriasDisponiveis?.data && Array.isArray(categoriasDisponiveis.data)) return categoriasDisponiveis.data;
        return [];
    }, [categoriasDisponiveis]);

    const fornecedoresArray = useMemo(() => {
        if (Array.isArray(fornecedoresDisponiveis)) return fornecedoresDisponiveis;
        if (fornecedoresDisponiveis?.data && Array.isArray(fornecedoresDisponiveis.data)) return fornecedoresDisponiveis.data;
        return [];
    }, [fornecedoresDisponiveis]);

    useEffect(() => {
        if (!isInitialized) return;
        const novaImagemPreview = processarImagensParaPreview(item);
        if (novaImagemPreview !== imagemPreview) {
            setImagemPreview(novaImagemPreview || defaultAvatar);
        }
    }, [isInitialized, item?.imagens, item?.imagensDaGaleria, item?.imagemPrincipal, imageUpdateTrigger]);

    useEffect(() => {
        if (isInitialized) return;

        if (!normalizedCurrent) {
            setItem({
                ...initialState,
                unidadesDetalhes: [{ unidade: '', preco: 0, quantidadeEmEstoque: 0, disponivel: true }]
            });
            setImagemPreview(defaultAvatar);
            setFormErrors({});
            setIsInitialized(true);
            return;
        }
        
        const dataIsLoading = loadingCategorias || loadingFornecedores;
        if (dataIsLoading) return;

        const categoriaId = extractId(normalizedCurrent.categoria);
        const fornecedorId = extractId(normalizedCurrent.metadata?.fornecedor);

        const unidadesDetalhesCorrigidas = (normalizedCurrent.unidadesDetalhes || []).map(ud => ({
            unidade: extractId(ud.unidade),
            preco: parseFloat(ud.preco) || 0,
            quantidadeEmEstoque: parseInt(ud.quantidadeEmEstoque) || 0,
            disponivel: ud.disponivel !== undefined ? ud.disponivel : true
        }));

        if (unidadesDetalhesCorrigidas.length === 0) {
            unidadesDetalhesCorrigidas.push({ unidade: '', preco: 0, quantidadeEmEstoque: 0, disponivel: true });
        }

        let descricaoCorrigida = '';
        if (normalizedCurrent.descricao !== undefined && normalizedCurrent.descricao !== null) {
            descricaoCorrigida = normalizedCurrent.descricao;
        } else if (normalizedCurrent.description !== undefined) {
            descricaoCorrigida = normalizedCurrent.description;
        } else if (normalizedCurrent.desc !== undefined) {
            descricaoCorrigida = normalizedCurrent.desc;
        }
        descricaoCorrigida = String(descricaoCorrigida || '');

        const tagsCorrigidas = Array.isArray(normalizedCurrent.tags) 
            ? normalizedCurrent.tags.filter(tag => tag && typeof tag === 'string')
            : [];

        const nomeCorrigido = normalizedCurrent.nome || '';
        const skuCorrigido = normalizedCurrent.sku || '';

        const itemData = {
            ...initialState,
            ...normalizedCurrent,
            _id: normalizedCurrent._id,
            nome: nomeCorrigido,
            descricao: descricaoCorrigida,
            sku: skuCorrigido,
            categoria: categoriaId,
            tags: tagsCorrigidas,
            controlaEstoque: normalizedCurrent.controlaEstoque !== undefined ? normalizedCurrent.controlaEstoque : true,
            ativo: normalizedCurrent.ativo !== undefined ? normalizedCurrent.ativo : true,
            visivelParaCliente: normalizedCurrent.visivelParaCliente !== undefined ? normalizedCurrent.visivelParaCliente : true,
            metadata: { 
                ...initialState.metadata,
                ...normalizedCurrent.metadata,
                fornecedor: fornecedorId,
            },
            unidadesDetalhes: unidadesDetalhesCorrigidas,
            imagens: normalizedCurrent.imagens || [],
            imagensDaGaleria: normalizedCurrent.imagensDaGaleria || [],
            imagemPrincipal: normalizedCurrent.imagemPrincipal || null,
        };
        
        setItem(itemData);
        
        const imagemPreviewUrl = processarImagensParaPreview(normalizedCurrent);
        setImagemPreview(imagemPreviewUrl || defaultAvatar);
            
        setFormErrors({});
        setIsInitialized(true);
        
    }, [normalizedCurrent, isInitialized, categoriasArray, fornecedoresArray, loadingCategorias, loadingFornecedores]);

    useEffect(() => {
        if (isInitialized && normalizedCurrent) {
            const currentChanged = 
                (normalizedCurrent._id && !item._id) || 
                (!normalizedCurrent._id && item._id) || 
                (normalizedCurrent._id !== item._id); 
            
            if (currentChanged) {
                setIsInitialized(false);
            }
        }
    }, [normalizedCurrent?._id, item._id, isInitialized]);

    // ✅ CORREÇÃO CRÍTICA AQUI: Reduzir Arrays de Objetos Populares para apenas Array de Strings (IDs)
    const limparDadosParaEnvio = useCallback((dados) => {
        const dadosLimpos = JSON.parse(JSON.stringify(dados));
        
        if (dadosLimpos.descricao === undefined || dadosLimpos.descricao === null) {
            dadosLimpos.descricao = '';
        }
        
        if (Array.isArray(dadosLimpos.tags)) {
            dadosLimpos.tags = dadosLimpos.tags.filter(tag => tag && typeof tag === 'string' && tag.trim() !== '');
        } else {
            dadosLimpos.tags = [];
        }
        
        if (dadosLimpos.metadata) {
            if (dadosLimpos.metadata.fornecedor === '' || !isValidObjectId(dadosLimpos.metadata.fornecedor)) {
                delete dadosLimpos.metadata.fornecedor;
            }
        }
        
        if (dadosLimpos.categoria && !isValidObjectId(dadosLimpos.categoria)) {
            delete dadosLimpos.categoria;
        }
        
        if (Array.isArray(dadosLimpos.unidadesDetalhes)) {
            dadosLimpos.unidadesDetalhes = dadosLimpos.unidadesDetalhes.map(ud => ({
                ...ud,
                unidade: isValidObjectId(ud.unidade) ? ud.unidade : '',
                preco: parseFloat(ud.preco) || 0,
                quantidadeEmEstoque: parseInt(ud.quantidadeEmEstoque) || 0,
                disponivel: ud.disponivel !== undefined ? ud.disponivel : true
            })).filter(ud => ud.unidade !== '');
        }

        // ✅ GARANTIA DO CASTING DE IMAGENS: Evitar enviar o objeto inteiro pro backend e dar erro "Cast to [ObjectId]"
        if (Array.isArray(dadosLimpos.imagens)) {
            dadosLimpos.imagens = dadosLimpos.imagens.map(img => extractId(img)).filter(id => id !== '');
        }
        
        if (Array.isArray(dadosLimpos.imagensDaGaleria)) {
            dadosLimpos.imagensDaGaleria = dadosLimpos.imagensDaGaleria.map(img => extractId(img)).filter(id => id !== '');
        }
        
        // ✅ Corrigir imagemPrincipal também
        if (dadosLimpos.imagemPrincipal && typeof dadosLimpos.imagemPrincipal === 'object') {
            dadosLimpos.imagemPrincipal.refId = extractId(dadosLimpos.imagemPrincipal.refId);
            if (!dadosLimpos.imagemPrincipal.refId) {
                dadosLimpos.imagemPrincipal = null;
            }
        }

        const camposTexto = ['nome', 'descricao', 'sku'];
        camposTexto.forEach(campo => {
            if (dadosLimpos[campo] === undefined || dadosLimpos[campo] === null) {
                dadosLimpos[campo] = '';
            }
        });
        
        return dadosLimpos;
    }, []);

    const handleImageUpdate = useCallback((updatedItem) => {
        setItem(prev => {
            const novoItem = { 
                ...prev,
                imagens: updatedItem.imagens || prev.imagens || [],
                imagensDaGaleria: updatedItem.imagensDaGaleria || prev.imagensDaGaleria || [],
                imagemPrincipal: updatedItem.imagemPrincipal,
                nome: updatedItem.nome || prev.nome,
                descricao: updatedItem.descricao || prev.descricao,
                sku: updatedItem.sku || prev.sku,
                categoria: updatedItem.categoria || prev.categoria,
                controlaEstoque: updatedItem.controlaEstoque !== undefined ? updatedItem.controlaEstoque : prev.controlaEstoque,
                ativo: updatedItem.ativo !== undefined ? updatedItem.ativo : prev.ativo,
                visivelParaCliente: updatedItem.visivelParaCliente !== undefined ? updatedItem.visivelParaCliente : prev.visivelParaCliente,
                tags: updatedItem.tags || prev.tags,
                unidadesDetalhes: updatedItem.unidadesDetalhes || prev.unidadesDetalhes,
                metadata: updatedItem.metadata || prev.metadata
            };
            
            return novoItem;
        });
        
        setTimeout(() => {
            const novaImagemPreview = processarImagensParaPreview(updatedItem);
            if (novaImagemPreview !== imagemPreview) {
                setImagemPreview(novaImagemPreview || defaultAvatar);
            }
            setImageUpdateTrigger(prev => prev + 1);
        }, 50);
    }, [imagemPreview]);

    const onChange = useCallback(e => {
        const { name, value, type, checked } = e.target;
        setItem(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : value 
        }));
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    }, [formErrors]);

    const onChangeMetadata = useCallback(e => {
        const { name, value, type } = e.target;
        setItem(prev => ({
            ...prev,
            metadata: {
                ...prev.metadata,
                [name]: type === 'number' ? parseFloat(value) || 0 : value
            }
        }));
    }, []);

    const onChangeDimensoes = useCallback((campo, valor) => {
        setItem(prev => ({
            ...prev,
            metadata: {
                ...prev.metadata,
                dimensoes: {
                    ...prev.metadata.dimensoes,
                    [campo]: parseFloat(valor) || 0
                }
            }
        }));
    }, []);

    const adicionarTag = useCallback(() => {
        if (novaTag.trim() && !item.tags.includes(novaTag.trim())) {
            setItem(prev => ({ ...prev, tags: [...prev.tags, novaTag.trim()] }));
            setNovaTag('');
        }
    }, [novaTag, item.tags]);

    const removerTag = useCallback((tagParaRemover) => {
        setItem(prev => ({ ...prev, tags: prev.tags.filter(tag => tag !== tagParaRemover) }));
    }, []);

    const handleTagKeyPress = useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            adicionarTag();
        }
    }, [adicionarTag]);

    const handleAddUnidadeDetalhe = useCallback(() => {
        setItem(prev => ({
            ...prev,
            unidadesDetalhes: [...prev.unidadesDetalhes, { 
                unidade: '', preco: 0, quantidadeEmEstoque: 0, disponivel: true 
            }]
        }));
    }, []);

    const handleRemoveUnidadeDetalhe = useCallback((index) => {
        setItem(prev => ({
            ...prev,
            unidadesDetalhes: prev.unidadesDetalhes.filter((_, i) => i !== index)
        }));
    }, []);

    const handleUnidadeDetalheChange = useCallback((index, field, value) => {
        setItem(prev => {
            const newUnidadesDetalhes = [...prev.unidadesDetalhes];
            newUnidadesDetalhes[index] = {
                ...newUnidadesDetalhes[index],
                [field]: field === 'preco' ? parseFloat(value) || 0 : 
                         field === 'quantidadeEmEstoque' ? parseInt(value) || 0 : 
                         field === 'disponivel' ? value === true || value === 'true' : value
            };
            return { ...prev, unidadesDetalhes: newUnidadesDetalhes };
        });
    }, []);

    const validarFormulario = useCallback(() => {
        const errors = {};
        if (!item.nome || !item.nome.trim()) errors.nome = 'Nome do produto é obrigatório';
        if (!item.categoria || !isValidObjectId(item.categoria)) errors.categoria = 'Selecione uma categoria válida';
        if (item.unidadesDetalhes.length === 0) errors.unidades = 'Configure pelo menos uma unidade para o produto';

        item.unidadesDetalhes.forEach((ud, index) => {
            if (!ud.unidade || !isValidObjectId(ud.unidade)) errors[`unidade_${index}`] = 'Selecione uma unidade válida';
            if (ud.preco === undefined || ud.preco === null || ud.preco < 0) errors[`preco_${index}`] = 'Preço deve ser maior ou igual a zero';
        });
        if (!isValidAccountId(accountId)) errors.account = 'ID da conta não está disponível. Recarregue a página.';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    }, [item, accountId]);

    const onSubmit = async e => {
        e.preventDefault();

        if (!validarFormulario()) {
            setAlert('Por favor, corrija os erros no formulário', 'danger');
            if (formErrors.account || formErrors.nome || formErrors.categoria) goToTab('basicos');
            else if (formErrors.unidades) goToTab('unidades');
            return;
        }

        const dadosParaEnviar = {
            _id: item._id,
            nome: item.nome || '',
            descricao: item.descricao || '',
            sku: item.sku || '',
            categoria: item.categoria || '',
            controlaEstoque: item.controlaEstoque !== undefined ? item.controlaEstoque : true,
            ativo: item.ativo !== undefined ? item.ativo : true,
            visivelParaCliente: item.visivelParaCliente !== undefined ? item.visivelParaCliente : true,
            tags: Array.isArray(item.tags) ? item.tags.filter(tag => tag && tag.trim() !== '') : [],
            unidadesDetalhes: item.unidadesDetalhes || [],
            imagens: item.imagens || [],
            imagensDaGaleria: item.imagensDaGaleria || [],
            imagemPrincipal: item.imagemPrincipal || null,
            metadata: {
                tipoItem: item.metadata?.tipoItem || 'fisico',
                peso: parseFloat(item.metadata?.peso) || 0,
                dimensoes: {
                    comprimento: parseFloat(item.metadata?.dimensoes?.comprimento) || 0,
                    largura: parseFloat(item.metadata?.dimensoes?.largura) || 0,
                    altura: parseFloat(item.metadata?.dimensoes?.altura) || 0
                },
                marca: item.metadata?.marca || '',
                modelo: item.metadata?.modelo || '',
                codigoBarras: item.metadata?.codigoBarras || '',
                fornecedor: item.metadata?.fornecedor || '',
                custo: parseFloat(item.metadata?.custo) || 0,
                margemLucro: parseFloat(item.metadata?.margemLucro) || 0
            }
        };

        const dadosLimpos = limparDadosParaEnvio(dadosParaEnviar);

        const mutationOptions = { 
            onSuccess: (data) => {
                const message = isEditing ? 'Produto atualizado com sucesso!' : 'Produto criado com sucesso!';
                setAlert(message, 'success');
                if (onSaveSuccess) onSaveSuccess(data.data);
            },
            onError: (error) => {
                const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Erro ao salvar produto';
                setAlert(errorMessage, 'danger');
            }
        };
        
        try {
            if (isEditing) {
                await updateItemVenda.mutateAsync({ 
                    id: normalizedCurrent._id,
                    itemData: dadosLimpos 
                }, mutationOptions);
            } else {
                await addItemVenda.mutateAsync(dadosLimpos, mutationOptions);
            }
        } catch (error) {
            console.error('❌ [ItemVendaForm] Erro no envio do formulário:', error);
        }
    };

    const handleClose = useCallback(() => {
        setIsInitialized(false);
        if (onCloseForm) onCloseForm();
    }, [onCloseForm]);

    const overallLoading = loadingMutation || 
                           (isValidAccountId(accountId) && (loadingUnidades || loadingCategorias || loadingFornecedores));

    if (!isValidAccountId(accountId)) {
        return (
            <div className="panel-state-wrapper text-center p-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <AlertCircle className="inline text-yellow-500 mr-2" size={20} />
                    <span className="text-yellow-700 font-medium">Aguardando carregamento da conta...</span>
                    <div className="mt-2"><Loader2 className="animate-spin inline mr-2 text-primary"/> Carregando informações da conta...</div>
                </div>
            </div>
        );
    }

    const hasDataErrors = isErrorUnidadesQuery || isErrorCategoriasQuery || isErrorFornecedoresQuery;

    return (
        <form onSubmit={onSubmit} noValidate>
            <div className="form-header">
                <h3 className="form-title">
                    {isEditing ? `Editar Produto: ${item.nome}` : 'Novo Produto'}
                </h3>
                <button type="button" onClick={handleClose} title="Fechar" className="form-close-button"><XCircle size={24} /></button>
            </div>

            {hasDataErrors && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start">
                        <AlertCircle size={20} className="text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                        <div>
                            <h4 className="font-medium text-red-900 mb-1">Erro ao carregar dados</h4>
                            <ul className="text-sm text-red-700 space-y-1">
                                {isErrorUnidadesQuery && <li>• Erro ao carregar unidades: {errorUnidades?.message}</li>}
                                {isErrorCategoriasQuery && <li>• Erro ao carregar categorias: {errorCategorias?.message}</li>}
                                {isErrorFornecedoresQuery && <li>• Erro ao carregar fornecedores: {errorFornecedores?.message}</li>}
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {formErrors.account && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center">
                        <AlertCircle size={20} className="text-red-500 mr-3 flex-shrink-0" />
                        <span className="text-red-700 font-medium">{formErrors.account}</span>
                    </div>
                </div>
            )}

            <FormStepper
                currentStep={currentStep}
                steps={STEP_LABELS}
                stepErrors={[
                    !!(formErrors.nome || formErrors.categoria || formErrors.account),
                    false,
                    !!formErrors.unidades,
                ]}
            />

            <div className="tab-content">
                {STEP_IDS[currentStep - 1] === 'basicos' && (
                    <>
                        <fieldset className="form-section">
                            <legend className="form-section-title">Informações do Produto</legend>
                            <div className="form-grid-container">
                                <div className="form-group full-width">
                                    <label htmlFor="nome" className="required">Nome do Produto</label>
                                    <input type="text" id="nome" name="nome" value={item.nome} onChange={onChange} required className={`form-input ${formErrors.nome ? 'input-error' : ''}`} placeholder="Digite o nome do produto" maxLength="200" />
                                    {formErrors.nome && <div className="form-error">{formErrors.nome}</div>}
                                    <div className="form-hint">{item.nome.length}/200 caracteres</div>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="categoria" className="required">Categoria</label>
                                    <select id="categoria" name="categoria" value={item.categoria} onChange={onChange} required className={`form-input ${formErrors.categoria ? 'input-error' : ''}`} disabled={loadingCategorias} >
                                        <option value="">Selecione uma categoria</option>
                                        {categoriasArray.map(categoria => (<option key={extractId(categoria)} value={extractId(categoria)}>{categoria.nome}</option>))}
                                    </select>
                                    {formErrors.categoria && <div className="form-error">{formErrors.categoria}</div>}
                                    {loadingCategorias && <div className="form-hint"><Loader2 size={14} className="animate-spin inline mr-1" /> Carregando categorias...</div>}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="sku">SKU (Código)</label>
                                    <input type="text" id="sku" name="sku" value={item.sku} onChange={onChange} className="form-input" placeholder="Código do produto"/>
                                </div>
                                <div className="form-group full-width">
                                    <label htmlFor="descricao">Descrição</label>
                                    <textarea id="descricao" name="descricao" value={item.descricao} onChange={onChange} rows="3" className="form-input" placeholder="Descrição do produto" maxLength="500" />
                                    <div className="form-hint">{item.descricao.length}/500 caracteres</div>
                                </div>
                                
                                <div className="form-group full-width">
                                    <label className="form-section-title">Imagem Principal</label>
                                    <div className="flex items-center gap-4 mt-2">
                                        <img 
                                            key={imagemPreview} 
                                            src={imagemPreview} 
                                            alt="Imagem Principal do Produto" 
                                            className="w-24 h-24 rounded-lg object-cover border" 
                                            onError={(e) => { 
                                                e.target.src = defaultAvatar;
                                            }} 
                                        />
                                        <div>
                                            <p className="font-medium text-gray-700">{imagemPreview && imagemPreview !== defaultAvatar ? 'Imagem Principal' : 'Sem Imagem'}</p>
                                            <p className="form-hint">{isEditing ? "As imagens podem ser editadas na lista de produtos." : "Salve o produto para poder adicionar imagens."}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-group full-width">
                                    <label htmlFor="tags">Tags</label>
                                    <div className="flex gap-2 mb-2">
                                        <input type="text" value={novaTag} onChange={(e) => setNovaTag(e.target.value)} onKeyPress={handleTagKeyPress} className="form-input flex-1" placeholder="Adicionar tag..." />
                                        <button type="button" onClick={adicionarTag} className="action-button action-button-secondary" disabled={!novaTag.trim()}><Plus size={16} /></button>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {item.tags.map((tag, index) => (<span key={index} className="tag">{tag}<button type="button" onClick={() => removerTag(tag)} className="tag-remove"><Minus size={12} /></button></span>))}
                                        {item.tags.length === 0 && <span className="text-sm text-gray-500">Nenhuma tag adicionada</span>}
                                    </div>
                                </div>
                            </div>
                        </fieldset>
                        <fieldset className="form-section">
                            <legend className="form-section-title">Configurações</legend>
                            <div className="form-grid-container">
                                <div className="form-group">
                                    <label className="checkbox-label"><input type="checkbox" id="controlaEstoque" name="controlaEstoque" checked={item.controlaEstoque} onChange={onChange} /><span className="checkbox-custom"></span><div><div className="font-medium">Controlar Estoque</div><div className="text-sm text-gray-500">Controlar quantidade em estoque</div></div></label>
                                </div>
                                <div className="form-group">
                                    <label className="checkbox-label"><input type="checkbox" id="ativo" name="ativo" checked={item.ativo} onChange={onChange} /><span className="checkbox-custom"></span><div><div className="font-medium">Produto Ativo</div><div className="text-sm text-gray-500">Produto disponível no sistema</div></div></label>
                                </div>
                                <div className="form-group">
                                    <label className="checkbox-label"><input type="checkbox" id="visivelParaCliente" name="visivelParaCliente" checked={item.visivelParaCliente} onChange={onChange} /><span className="checkbox-custom"></span><div><div className="font-medium">Visível para Clientes</div><div className="text-sm text-gray=500">Exibir para clientes finais</div></div></label>
                                </div>
                            </div>
                        </fieldset>
                    </>
                )}
                {STEP_IDS[currentStep - 1] === 'detalhes' && (
                    <fieldset className="form-section">
                        <legend className="form-section-title"><Package size={20} className="inline mr-2"/>Detalhes do Produto</legend>
                        <div className="form-grid-container">
                            <div className="form-group"><label htmlFor="tipoItem" className="required">Tipo do Item</label><select id="tipoItem" name="tipoItem" value={item.metadata.tipoItem} onChange={onChangeMetadata} className="form-input" ><option value="fisico">Produto Físico</option><option value="digital">Produto Digital</option></select></div>
                            <div className="form-group"><label htmlFor="peso"><Scale size={16} className="inline mr-1"/>Peso (gramas)</label><input type="number" id="peso" name="peso" value={item.metadata.peso} onChange={onChangeMetadata} min="0" step="1" className="form-input" placeholder="0" /></div>
                            <div className="form-group full-width"><label className="form-subtitle"><Box size={16} className="inline mr-1"/>Dimensões (cm)</label><div className="grid grid-cols-3 gap-3"><div><label htmlFor="comprimento" className="text-sm">Comprimento</label><input type="number" id="comprimento" value={item.metadata.dimensoes.comprimento} onChange={(e) => onChangeDimensoes('comprimento', e.target.value)} min="0" step="0.1" className="form-input" placeholder="0" /></div><div><label htmlFor="largura" className="text-sm">Largura</label><input type="number" id="largura" value={item.metadata.dimensoes.largura} onChange={(e) => onChangeDimensoes('largura', e.target.value)} min="0" step="0.1" className="form-input" placeholder="0" /></div><div><label htmlFor="altura" className="text-sm">Altura</label><input type="number" id="altura" value={item.metadata.dimensoes.altura} onChange={(e) => onChangeDimensoes('altura', e.target.value)} min="0" step="0.1" className="form-input" placeholder="0" /></div></div></div>
                            <div className="form-group"><label htmlFor="marca"><Type size={16} className="inline mr-1"/>Marca</label><input type="text" id="marca" name="marca" value={item.metadata.marca} onChange={onChangeMetadata} className="form-input" placeholder="Marca do produto" maxLength="100" /></div>
                            <div className="form-group"><label htmlFor="modelo"><Hash size={16} className="inline mr-1"/>Modelo</label><input type="text" id="modelo" name="modelo" value={item.metadata.modelo} onChange={onChangeMetadata} className="form-input" placeholder="Modelo do produto" maxLength="100" /></div>
                            <div className="form-group"><label htmlFor="codigoBarras"><Barcode size={16} className="inline mr-1"/>Código de Barras</label><input type="text" id="codigoBarras" name="codigoBarras" value={item.metadata.codigoBarras} onChange={onChangeMetadata} className="form-input" placeholder="Código de barras" /></div>
                            <div className="form-group"><label htmlFor="fornecedor"><Truck size={16} className="inline mr-1"/>Fornecedor</label><select id="fornecedor" name="fornecedor" value={item.metadata.fornecedor} onChange={onChangeMetadata} className="form-input" disabled={loadingFornecedores} ><option value="">Selecione um fornecedor</option>{fornecedoresArray.map(fornecedor => (<option key={extractId(fornecedor)} value={extractId(fornecedor)}>{fornecedor.nome}</option>))}</select>{loadingFornecedores && <div className="form-hint"><Loader2 size={14} className="animate-spin inline mr-1" /> Carregando fornecedores...</div>}</div>
                            <div className="form-group"><label htmlFor="custo"><DollarSign size={16} className="inline mr-1"/>Custo ({currencySymbol})</label><input type="number" id="custo" name="custo" value={item.metadata.custo} onChange={onChangeMetadata} min="0" step="0.01" className="form-input" placeholder="0.00" /></div>
                            <div className="form-group"><label htmlFor="margemLucro"><List size={16} className="inline mr-1"/>Margem de Lucro (%)</label><input type="number" id="margemLucro" name="margemLucro" value={item.metadata.margemLucro} onChange={onChangeMetadata} min="0" step="0.1" className="form-input" placeholder="0" /></div>
                        </div>
                    </fieldset>
                )}
                {STEP_IDS[currentStep - 1] === 'unidades' && (
                    <fieldset className="form-section">
                        <legend className="form-section-title">Preço e Estoque por Unidade</legend>
                        {formErrors.unidades && <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4"><div className="flex items-center"><AlertCircle size={16} className="text-red-500 mr-2" /><span className="text-red-700 font-medium">{formErrors.unidades}</span></div></div>}
                        {loadingUnidades ? (<div className="text-center p-4"><Loader2 className="animate-spin inline mr-2 text-primary"/> Carregando unidades...</div>) : (isErrorUnidadesQuery || unidadesArray.length === 0) ? (<div className="text-center p-4 text-red-500 bg-red-50 rounded-lg">{isErrorUnidadesQuery ? '❌ Erro ao carregar unidades' : "📝 Nenhuma unidade disponível"}</div>) : (
                            <>
                                {item.unidadesDetalhes.map((unidadeDetalhe, index) => (
                                    <div key={index} className="indented-panel mb-4 p-4 border rounded-lg bg-gray-50">
                                        <div className="flex justify-between items-center mb-3">
                                            <h4 className="font-semibold text-primary">Unidade {index + 1}</h4>
                                            {item.unidadesDetalhes.length > 1 && <button type="button" onClick={() => handleRemoveUnidadeDetalhe(index)} className="p-1 text-red-600 hover:text-red-800" title="Remover unidade"><Minus size={20} /></button>}
                                        </div>
                                        <div className="form-grid-container">
                                            <div className="form-group">
                                                <label className="required">Unidade</label>
                                                <select value={unidadeDetalhe.unidade} onChange={(e) => handleUnidadeDetalheChange(index, 'unidade', e.target.value)} required className={`form-input ${formErrors[`unidade_${index}`] ? 'input-error' : ''}`} >
                                                    <option value="">Selecione uma unidade</option>
                                                    {unidadesArray.map(unidade => {
                                                        const localidade = unidade.endereco?.localidade || unidade.localidade || '';
                                                        const uf = unidade.endereco?.uf || unidade.uf || '';
                                                        const sufixoEndereco = localidade ? `- ${localidade}${uf ? `/${uf}` : ''}` : '';
                                                        
                                                        return (
                                                            <option key={extractId(unidade)} value={extractId(unidade)}>
                                                                {unidade.nome} {sufixoEndereco}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                                {formErrors[`unidade_${index}`] && <div className="form-error">{formErrors[`unidade_${index}`]}</div>}
                                            </div>
                                            <div className="form-group"><label className="required">Preço ({currencySymbol})</label><input type="number" step="0.01" min="0" value={unidadeDetalhe.preco} onChange={(e) => handleUnidadeDetalheChange(index, 'preco', e.target.value)} required className={`form-input ${formErrors[`preco_${index}`] ? 'input-error' : ''}`} placeholder="0.00" />{formErrors[`preco_${index}`] && <div className="form-error">{formErrors[`preco_${index}`]}</div>}</div>
                                            {item.controlaEstoque && (<div className="form-group"><label>Estoque</label><input type="number" min="0" value={unidadeDetalhe.quantidadeEmEstoque} onChange={(e) => handleUnidadeDetalheChange(index, 'quantidadeEmEstoque', e.target.value)} className="form-input" placeholder="0" /></div>)}
                                            <div className="form-group-checkbox col-span-full"><input type="checkbox" checked={unidadeDetalhe.disponivel} onChange={(e) => handleUnidadeDetalheChange(index, 'disponivel', e.target.checked)} /><label>Disponível para venda</label></div>
                                        </div>
                                    </div>
                                ))}
                                <button type="button" onClick={handleAddUnidadeDetalhe} className="action-button action-button-secondary mt-4 w-full" ><Plus size={18} className="mr-2"/> Adicionar Outra Unidade</button>
                            </>
                        )}
                    </fieldset>
                )}
            </div>
            
            <div className="form-actions">
                <button type="button" onClick={handleClose} className="action-button action-button-secondary" disabled={overallLoading}><XCircle size={18} className="icon"/> Cancelar</button>
                <FormStepNavigation
                    currentStep={currentStep}
                    totalSteps={STEP_LABELS.length}
                    onBack={() => setCurrentStep((s) => s - 1)}
                    onNext={() => setCurrentStep((s) => s + 1)}
                    isSubmitting={overallLoading}
                    submitLabel={overallLoading ? (isEditing ? 'Atualizando...' : 'Salvando...') : (isEditing ? 'Atualizar Produto' : 'Criar Produto')}
                />
            </div>
        </form>
    );
};

export default ItemVendaForm;